import { bookingDraftKey, inBookingPath } from "./public-address";
// A short-lived, tab-scoped draft. Never persist provider/capability tokens or files.
const key = bookingDraftKey;
const lifetime = 30 * 60_000;
const fields = [
  "name",
  "email",
  "phone",
  "organization",
  "organizationCountry",
  "organizationCountryChosen",
  "organizationCountrySource",
  "notes",
  "guestLocationProposal",
  "visitorAddressQuery",
  "guestLocationDetails",
  "visitorLocationDetailsAddress",
] as const;
export type JourneyDraft = {
  savedAt: number;
  path: string;
  step: 1 | 2 | 3;
  typeSlug: string;
  slot: string;
  locationId: string;
  cityPlaceId?: string;
  manualAddressConfirmed?: boolean;
  timeZone: string;
  fields: Record<string, string>;
  invitees: string[];
  hadAttachment: boolean;
  clientSubmissionId?: string;
  submissionSelection?: string;
};
export function readDraft(): JourneyDraft | null {
  try {
    const value: unknown = JSON.parse(sessionStorage.getItem(key) ?? "null");
    if (!value || typeof value !== "object") return null;
    const v = value as Partial<JourneyDraft>;
    if (
      typeof v.savedAt !== "number" ||
      Date.now() - v.savedAt > lifetime ||
      v.savedAt > Date.now() ||
      typeof v.path !== "string" ||
      !inBookingPath(v.path) ||
      ![1, 2, 3].includes(v.step ?? 0)
    ) {
      clearDraft();
      return null;
    }
    const clean = Object.fromEntries(
      fields.map((name) => [
        name,
        typeof v.fields?.[name] === "string"
          ? v.fields[name].slice(0, 4000)
          : "",
      ]),
    );
    return {
      savedAt: v.savedAt,
      path: v.path,
      step: v.step!,
      typeSlug: typeof v.typeSlug === "string" ? v.typeSlug : "",
      slot:
        typeof v.slot === "string" && Number.isFinite(Date.parse(v.slot))
          ? v.slot
          : "",
      locationId: typeof v.locationId === "string" ? v.locationId : "",
      cityPlaceId:
        typeof v.cityPlaceId === "string" ? v.cityPlaceId.slice(0, 200) : "",
      manualAddressConfirmed: v.manualAddressConfirmed === true,
      timeZone: typeof v.timeZone === "string" ? v.timeZone : "",
      fields: clean,
      invitees: Array.isArray(v.invitees)
        ? v.invitees
            .filter((s): s is string => typeof s === "string")
            .slice(0, 10)
            .map((s) => s.slice(0, 254))
        : [],
      hadAttachment: v.hadAttachment === true,
      clientSubmissionId:
        typeof v.clientSubmissionId === "string" &&
        /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
          v.clientSubmissionId,
        )
          ? v.clientSubmissionId
          : undefined,
      submissionSelection:
        typeof v.submissionSelection === "string" &&
        v.submissionSelection.length <= 4096
          ? v.submissionSelection
          : undefined,
    };
  } catch {
    return null;
  }
}
export function writeDraft(value: JourneyDraft): void {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* In-memory navigation still works in restricted browsers. */
  }
}
export function clearDraft(): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* Storage can be disabled. */
  }
}
export function captureContactFields(
  form: HTMLFormElement,
): Record<string, string> {
  const data = new FormData(form);
  const country = form.elements.namedItem(
    "organizationCountry",
  ) as HTMLSelectElement | null;
  data.set("organizationCountryChosen", country?.dataset.chosen ?? "false");
  data.set("organizationCountrySource", country?.dataset.source ?? "");
  return Object.fromEntries(
    fields
      .filter((name) => data.has(name))
      .map((name) => [name, String(data.get(name) ?? "")]),
  );
}
export function isPublicEmail(value: string): boolean {
  return /^[^\s@]+@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z](?:[a-z0-9-]*[a-z0-9])$/i.test(
    value.trim(),
  );
}
